/**
 * PATH: packages/ui/src/diagnostics/clientErrorReporterFetch.ts
 * FILE_ID: SHARED-DIAGNOSTICS-FETCH-002
 * PURPOSE: Isolate the patched-fetch diagnostics and timing seam.
 * WHY: Keeps the public reporter under the file-size ceiling without changing
 *      fetch callers or the reporter's failure-isolation contract.
 * FLOW: browser fetch → timing/correlation → diagnostic callbacks → caller.
 * RELATED: clientErrorReporter.ts; clientErrorReporter.test.ts.
 */
import type { FetchTiming } from './clientErrorReporterTypes'

export interface FetchResponseError {
  method: string
  url: string
  status: number
  excerpt: string
  durationMs: number
}

export interface FetchNetworkError {
  method: string
  url: string
  durationMs: number
  error: unknown
}

export interface InstallFetchInstrumentationArgs {
  getIngestUrl: () => string
  parseServerTiming: (response: Response) => number | undefined
  onFetchTiming: (timing: FetchTiming) => void
  shouldSkipDiagnostic: (status: number, url: string) => boolean
  onResponseError: (error: FetchResponseError) => void
  onNetworkError: (error: FetchNetworkError) => void
}

/** Patch browser fetch once; all instrumentation failures stay invisible to callers. */
export function installFetchInstrumentation({
  getIngestUrl,
  parseServerTiming,
  onFetchTiming,
  shouldSkipDiagnostic,
  onResponseError,
  onNetworkError,
}: InstallFetchInstrumentationArgs): typeof fetch | null {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return null

  const originalFetch = window.fetch.bind(window)
  window.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const method = (init?.method ?? 'GET').toUpperCase()
    const url = stringifyRequestUrl(input)
    const ingestUrl = getIngestUrl()
    if (url && ingestUrl && url.startsWith(ingestUrl)) {
      return originalFetch.call(window, input, init)
    }

    let correlationId: string | undefined
    let finalInit = init
    if ((typeof input === 'string' || input instanceof URL) && isSameOrigin(url)) {
      try {
        const headers = new Headers(init?.headers as HeadersInit | undefined)
        const existing = headers.get('X-Correlation-ID')
        if (existing) {
          correlationId = existing
        } else {
          correlationId = shortCorrelationId()
          headers.set('X-Correlation-ID', correlationId)
        }
        finalInit = { ...(init || {}), headers }
      } catch {
        correlationId = undefined
        finalInit = init
      }
    }

    const started = typeof performance !== 'undefined' ? performance.now() : Date.now()
    try {
      const response = await originalFetch.call(window, input, finalInit)
      const durationMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started
      onFetchTiming({
        url,
        urlTemplate: templatiseUrl(url),
        method,
        status: response.status,
        durationMs,
        serverTimingMs: parseServerTiming(response),
        ok: response.ok,
        correlationId,
      })

      if (!response.ok && response.status >= 400 && !shouldSkipDiagnostic(response.status, url)) {
        try {
          onResponseError({
            method,
            url,
            status: response.status,
            excerpt: await safeBodyExcerpt(response.clone()),
            durationMs: Math.round(
              (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started,
            ),
          })
        } catch {
          /* diagnostics must never break the fetch caller */
        }
      }
      return response
    } catch (error) {
      const durationMs = Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started,
      )
      onFetchTiming({
        url,
        urlTemplate: templatiseUrl(url),
        method,
        status: 0,
        durationMs,
        ok: false,
        correlationId,
      })
      try {
        onNetworkError({
          method,
          url,
          durationMs,
          error,
        })
      } catch {
        /* diagnostics must never replace the original network error */
      }
      throw error
    }
  }
  return originalFetch
}

function templatiseUrl(rawUrl: string): string {
  try {
    let path = rawUrl
    try {
      path = new URL(rawUrl, typeof window !== 'undefined' ? window.location.origin : 'http://x').pathname
    } catch {
      path = rawUrl.split('?')[0]
    }
    return path
      .split('/')
      .map((segment) => {
        if (/^\d+$/.test(segment)) return '{id}'
        if (/^[0-9a-fA-F]{8}-[0-9a-fA-F-]{20,}$/.test(segment)) return '{uuid}'
        if (/^[0-9a-fA-F]{16,}$/.test(segment)) return '{hash}'
        return segment
      })
      .join('/')
  } catch {
    return rawUrl
  }
}

function isSameOrigin(url: string): boolean {
  try {
    if (!url) return false
    if (url.startsWith('/') && !url.startsWith('//')) return true
    if (typeof window === 'undefined') return false
    return new URL(url, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

function shortCorrelationId(): string {
  try {
    return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  } catch {
    return `c-${Math.random().toString(36).slice(2, 12)}`
  }
}

function stringifyRequestUrl(input: RequestInfo | URL): string {
  try {
    if (typeof input === 'string') return input
    if (input instanceof URL) return input.toString()
    if (input && typeof (input as Request).url === 'string') return (input as Request).url
  } catch {
    /* ignore */
  }
  return ''
}

async function safeBodyExcerpt(response: Response): Promise<string> {
  try {
    const text = await response.text()
    if (!text) return ''
    return text.length > 1024 ? `${text.slice(0, 1024)}\u2026[truncated]` : text
  } catch {
    return ''
  }
}
